import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import adminClient, {
  removeStoredToken,
  setStoredToken,
} from '../api/adminClient';

interface LoginCredentials {
  username: string;
  password: string;
}

interface AuthResponse {
  accessToken: string;
}

interface AuthState {
  isAuthenticated: boolean;
  token: string | null;
  loading: boolean;
}

const getStoredToken = (): string | null => localStorage.getItem('adminToken');

export const useAuth = () => {
  const [authState, setAuthState] = useState<AuthState>({
    isAuthenticated: false,
    token: null,
    loading: true,
  });

  const navigate = useNavigate();

  const verifyToken = useCallback(async (token: string) => {
    try {
      const response = await adminClient.get('/admin/check', {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.status === 200) {
        setAuthState({
          isAuthenticated: true,
          token: getStoredToken(),
          loading: false,
        });
      }
    } catch {
      removeStoredToken();
      setAuthState({
        isAuthenticated: false,
        token: null,
        loading: false,
      });
    }
  }, []);

  useEffect(() => {
    const token = getStoredToken();
    if (token) {
      verifyToken(token);
    } else {
      setAuthState((prev) => ({ ...prev, loading: false }));
    }
  }, [verifyToken]);

  const login = async (
    credentials: LoginCredentials,
  ): Promise<{ success: boolean; error?: string }> => {
    try {
      setAuthState((prev) => ({ ...prev, loading: true }));

      const response = await adminClient.post<AuthResponse>(
        '/admin/login',
        credentials,
      );

      if (response.status === 201 && response.data.accessToken) {
        const { accessToken } = response.data;
        setStoredToken(accessToken);
        setAuthState({
          isAuthenticated: true,
          token: accessToken,
          loading: false,
        });
        return { success: true };
      }

      return { success: false, error: 'Неожиданная ошибка сервера' };
    } catch (error) {
      setAuthState((prev) => ({ ...prev, loading: false }));

      if (axios.isAxiosError(error)) {
        const message = error.response?.data?.message || 'Ошибка входа';
        return { success: false, error: message };
      }

      return { success: false, error: 'Произошла ошибка при входе' };
    }
  };

  const logout = async (): Promise<void> => {
    try {
      await adminClient.post('/admin/logout');
    } catch {
      // ignore logout network errors
    } finally {
      removeStoredToken();
      setAuthState({
        isAuthenticated: false,
        token: null,
        loading: false,
      });
      navigate('/admin/login');
    }
  };

  const refreshToken = async (): Promise<{
    success: boolean;
    error?: string;
  }> => {
    try {
      const response = await adminClient.post('/admin/refresh');

      if (response.status === 201 && response.data.accessToken) {
        const newToken = response.data.accessToken;
        setStoredToken(newToken);
        setAuthState((prev) => ({
          ...prev,
          token: newToken,
          isAuthenticated: true,
        }));
        return { success: true };
      }

      return { success: false, error: 'Не удалось обновить токен' };
    } catch (error) {
      if (axios.isAxiosError(error)) {
        const message =
          error.response?.data?.message || 'Ошибка обновления токена';
        return { success: false, error: message };
      }

      return {
        success: false,
        error: 'Произошла ошибка при обновлении токена',
      };
    }
  };

  return {
    ...authState,
    login,
    logout,
    refreshToken,
    getAuthenticatedApi: () => adminClient,
  };
};

export default useAuth;
